// One function per API operation; screens never build URLs themselves.
import { api, http, qs } from "./client";
import type {
  AdminAttempt, AttemptFilter, LessonStat, BroadcastInput, BroadcastKind, BroadcastResult, Course, CourseOrder, CourseTree, DashboardOverview, ExamFormat, Exercise,
  ExerciseTypeSchema, Language, Lesson, PreviewGradeResult,
  LessonContent, Level, List, LocalizedText, ManualPlan, NotificationAudience, OutboxItem, OutboxStatus, Page, Profile, ProgressSummary,
  PublishIssue, Reading, ReadingSummary, Session, Staff, StaffRole, Subscription, SubscriptionRecord, TestPushResult, Unit, User, UserStatus,
  Vocabulary, VocabularyUsage,
} from "./types";
import type { AuditAction, AuditEntityType, AuditEvent } from "./types";
import type { Feedback, FeedbackCategory, FeedbackPlatform, FeedbackStatus } from "./types";
import type { AnalyticsEventName, AnalyticsSeries, AnalyticsSummary } from "./types";
import type {
  AiFeature, AiFeatureOverride, AiModel, AiModelPrice, AiPlaygroundInput, AiPlaygroundResult, AiProvider, AiProviderInput,
  AiProviderTestResult, AiSettings, AiSettingsInput, AiUsageGroupBy, AiUsageReport,
} from "./types";
import type {
  AiContentReview, AiDistractorsInput, AiExerciseDraftsInput, AiExerciseDraftsResult, AiFeedbackSummary, AiFeedbackSummaryInput,
  AiNotificationDraftInput, AiNotificationVariant, AiReadingDraft, AiReadingDraftInput, AiTranslateInput, AiTranslateResult,
  AiUnitTheoryInput, UnitTheory,
} from "./types";
import type { AiRoleplay, AiRoleplayInput, AiRoleplayStatus } from "./types"; // AI group E (speaking)
import type { EssayGradeInput, EssayGradeResult, EssayRef, PendingEssay } from "./types";
import type { AdminClassroom, AdminClassroomDetail, ClassroomStatus, TeacherGrant } from "./types";
import type { AdminExamAttempt, AdminExamAttemptDetail, ExamAttemptFilter, LearnerSprints, MistakeOverview } from "./types";
import type { Promo, PromoInput } from "./types";
import type { CourseDraft, CourseImport, CourseImportMode, CourseImportStatus, CourseImportSummary, ExtractedDocument } from "./types";
import type {
  AdminOrg, AdminOrgInput, CenterOverview, ClassReport, InviteStatus, OrgActivityDay, OrgBranch, OrgClass, OrgInvite, OrgStudent,
  OrgSummary, OrgTeacher, PricingTier, LinkableClass,
} from "./types";
import { uploadForm } from "./upload";

export const authApi = {
  google: (idToken: string) => api.request<Session>("POST", "/v1/auth/staff/google", { id_token: idToken }, { auth: false }),
  dev: (email: string, role: StaffRole) => api.request<Session>("POST", "/v1/auth/staff/dev", { email, role }, { auth: false }),
  /** Revokes the refresh cookie's session and clears the cookie. */
  logout: () => api.request<void>("POST", "/v1/auth/staff/logout", {}, { auth: false }),
  me: () => http.get<Staff>("/v1/admin/me"),
};

/** Dashboard team accounts (editors and admins). Admin only. */
export const staffApi = {
  list: (p: { q?: string; role?: string; limit?: number; offset?: number }) =>
    http.get<Page<Staff>>(`/v1/admin/staff${qs(p)}`),
  invite: (body: { email: string; role: StaffRole }) => http.post<Staff>("/v1/admin/staff", body),
  update: (id: string, body: { role?: StaffRole; status?: UserStatus }) => http.patch<Staff>(`/v1/admin/staff/${id}`, body),
};

/** Learner accounts. Learners have no roles: staff permissions live in staffApi. */
export const usersApi = {
  list: (p: { q?: string; status?: UserStatus | ""; premium?: "true" | "false" | ""; limit?: number; offset?: number }) =>
    http.get<Page<User>>(`/v1/admin/users${qs(p)}`),
  get: (id: string) => http.get<Profile>(`/v1/admin/users/${id}`),
  update: (id: string, body: { status: UserStatus }) => http.patch<User>(`/v1/admin/users/${id}`, body),
  grantSubscription: (id: string, body: { plan: ManualPlan; current_period_end: string }) =>
    http.post<Subscription>(`/v1/admin/users/${id}/subscriptions`, body),
  subscriptions: (id: string) => http.get<List<SubscriptionRecord>>(`/v1/admin/users/${id}/subscriptions`),
  /** The reason goes in the query: proxies may drop the body of a DELETE. */
  revokeSubscription: (id: string, subscriptionId: string, reason: string) =>
    http.del<SubscriptionRecord>(`/v1/admin/users/${id}/subscriptions/${subscriptionId}${qs({ reason })}`),
  progress: (id: string) => http.get<ProgressSummary>(`/v1/admin/users/${id}/progress`),
  /** Read-only: finished unit sprints and each unit's record. */
  sprints: (id: string) => http.get<LearnerSprints>(`/v1/admin/users/${id}/sprints`),
  /** Read-only: the mistake notebook summarized (count, lessons, most frequent). */
  mistakes: (id: string, p: { limit?: number } = {}) => http.get<MistakeOverview>(`/v1/admin/users/${id}/mistakes${qs(p)}`),
  /** Classroom teacher permission. Granting twice returns the existing grant. */
  grantTeacher: (id: string, body: { note?: string }) => http.put<TeacherGrant>(`/v1/admin/users/${id}/teacher`, body),
  /** Takes the permission back; the teacher's active classes become archived. */
  revokeTeacher: (id: string) => http.del(`/v1/admin/users/${id}/teacher`),
};

export type ClassroomFilter = { q?: string; status?: ClassroomStatus | ""; limit?: number; offset?: number };

/** Teachers' classes ("Lớp học"). Admin only: members are listed with their e-mail. */
export const classroomsApi = {
  list: (p: ClassroomFilter) => http.get<Page<AdminClassroom>>(`/v1/admin/classrooms${qs(p)}`),
  get: (id: string) => http.get<AdminClassroomDetail>(`/v1/admin/classrooms/${id}`),
  /** Read-only for teacher and learners until unsuspended; reason 1–500 characters. */
  suspend: (id: string, reason: string) => http.post<AdminClassroom>(`/v1/admin/classrooms/${id}/suspend`, { reason }),
  unsuspend: (id: string) => http.post<AdminClassroom>(`/v1/admin/classrooms/${id}/unsuspend`),
  removePost: (id: string, postId: string) => http.del(`/v1/admin/classrooms/${id}/posts/${postId}`),
};

export const languagesApi = {
  list: () => http.get<List<Language>>("/v1/admin/languages"),
  save: (l: Language) => http.put<Language>(`/v1/admin/languages/${l.code}`, l),
  levels: (code: string) => http.get<List<Level>>(`/v1/admin/languages/${code}/levels`),
  createLevel: (code: string, body: { code: string; name: LocalizedText; ordinal: number }) =>
    http.post<Level>(`/v1/admin/languages/${code}/levels`, body),
  updateLevel: (code: string, id: string, body: { code?: string; name?: LocalizedText }) =>
    http.patch<Level>(`/v1/admin/languages/${code}/levels/${id}`, body),
  /** 409 level_in_use (details: LevelUsage) while content still carries the level. */
  removeLevel: (code: string, id: string) => http.del(`/v1/admin/languages/${code}/levels/${id}`),
  reorderLevels: (code: string, ids: string[]) => http.put<void>(`/v1/admin/languages/${code}/levels/order`, { level_ids: ids }),
};

export type CourseInput = Partial<Omit<Course, "id" | "published_at" | "updated_at">>;
export type LessonInput = Partial<Pick<Lesson, "kind" | "title" | "objective" | "image_url" | "estimated_minutes" | "status" | "sort_order" | "time_limit_minutes" | "pass_score_percent" | "exam_format">>;
export type ExerciseInput = Pick<Exercise, "type" | "skill" | "prompt" | "content" | "solution" | "points" | "sort_order" | "section"> & {
  /** '' clears it; omitted (AI drafts) = none. */
  image_url?: string;
};
export type VocabularyInput = Omit<Vocabulary, "id">;
/** PATCH semantics: omitted fields are kept; `theory` replaces the whole theory (`{}` clears it). */
export type UnitInput = Partial<Pick<Unit, "title" | "description" | "image_url" | "sort_order" | "theory">>;

export const coursesApi = {
  /** `hsk3_level`: 1–7, or "none" for untagged courses. */
  list: (p: { language?: string; status?: string; q?: string; hsk3_level?: string; limit?: number; offset?: number }) =>
    http.get<Page<Course>>(`/v1/admin/courses${qs(p)}`),
  tree: (id: string) => http.get<CourseTree>(`/v1/admin/courses/${id}`),
  create: (body: CourseInput) => http.post<Course>("/v1/admin/courses", body),
  update: (id: string, body: CourseInput) => http.patch<Course>(`/v1/admin/courses/${id}`, body),
  remove: (id: string) => http.del(`/v1/admin/courses/${id}`),
  createUnit: (courseId: string, body: UnitInput & { title: LocalizedText }) =>
    http.post<Unit>(`/v1/admin/courses/${courseId}/units`, body),
  updateUnit: (id: string, body: UnitInput) =>
    http.patch<Unit>(`/v1/admin/units/${id}`, body),
  removeUnit: (id: string) => http.del(`/v1/admin/units/${id}`),
  reorder: (id: string, body: CourseOrder) => http.put<void>(`/v1/admin/courses/${id}/order`, body),
  publishCheck: (id: string) => http.get<{ issues: PublishIssue[] }>(`/v1/admin/courses/${id}/publish-check`),
};

export interface PinyinOptions {
  mode?: "sentence" | "word";
  language?: string;
}

export const lessonsApi = {
  create: (unitId: string, body: LessonInput) => http.post<Lesson>(`/v1/admin/units/${unitId}/lessons`, body),
  get: (id: string) => http.get<LessonContent>(`/v1/admin/lessons/${id}`),
  update: (id: string, body: LessonInput) => http.patch<Lesson>(`/v1/admin/lessons/${id}`, body),
  remove: (id: string) => http.del(`/v1/admin/lessons/${id}`),
  setVocabulary: (id: string, ids: string[]) => http.put<void>(`/v1/admin/lessons/${id}/vocabulary`, { vocabulary_ids: ids }),
  exerciseTypes: () => http.get<List<ExerciseTypeSchema>>("/v1/admin/exercise-types"),
  /** The HSK exam formats a test can follow (sections, timings, scale). */
  examFormats: () => http.get<List<ExamFormat>>("/v1/admin/exam-formats"),
  /** Grades an answer against an unsaved exercise (nothing is stored). */
  previewGrade: (body: Pick<Exercise, "type" | "content" | "solution" | "points"> & { answer: unknown }) =>
    http.post<PreviewGradeResult>("/v1/admin/exercises/preview-grade", body),
  /**
   * Generated pinyin of each text ("" without Chinese), for the "fill pinyin" buttons: word by word, cut by the
   * vocabulary of `language`, or one word with `mode: "word"` (a vocabulary term). `review` lists, per text, the
   * polyphonic characters whose reading is a guess.
   */
  pinyin: (texts: string[], opts: PinyinOptions = {}) =>
    http.post<{ readings: string[]; review?: string[][] }>("/v1/admin/pinyin", { texts, ...opts }),
  createExercise: (lessonId: string, body: ExerciseInput) => http.post<Exercise>(`/v1/admin/lessons/${lessonId}/exercises`, body),
  updateExercise: (id: string, body: ExerciseInput) => http.put<Exercise>(`/v1/admin/exercises/${id}`, body),
  removeExercise: (id: string) => http.del(`/v1/admin/exercises/${id}`),
  reorderExercises: (lessonId: string, ids: string[]) =>
    http.put<void>(`/v1/admin/lessons/${lessonId}/exercises/order`, { exercise_ids: ids }),
};

export const vocabularyApi = {
  /** `term` matches exactly (duplicate check); `q` searches term, reading and meanings; `hsk3_level`: 1–7 or "none". */
  list: (p: { language?: string; level_id?: string; q?: string; term?: string; hsk3_level?: string; limit?: number; offset?: number }) =>
    http.get<Page<Vocabulary>>(`/v1/admin/vocabulary${qs(p)}`),
  create: (body: VocabularyInput) => http.post<Vocabulary>("/v1/admin/vocabulary", body),
  update: (id: string, body: VocabularyInput) => http.put<Vocabulary>(`/v1/admin/vocabulary/${id}`, body),
  usage: (id: string) => http.get<List<VocabularyUsage>>(`/v1/admin/vocabulary/${id}/usage`),
  /** A word used by lessons needs `force` (it is then removed from them); otherwise 409 vocabulary_in_use. */
  remove: (id: string, force = false) => http.del(`/v1/admin/vocabulary/${id}${force ? "?force=true" : ""}`),
};

export const reportingApi = {
  dashboard: () => http.get<DashboardOverview>("/v1/admin/dashboard"),
  /** Admin only: names learners. */
  attempts: (p: AttemptFilter & { limit?: number; offset?: number }) =>
    http.get<Page<AdminAttempt>>(`/v1/admin/attempts${qs({ ...p })}`),
  /** Admin only. CSV (UTF-8 with BOM) of the filtered attempts; `timezone` sets the time column's zone. */
  attemptsCsv: (p: AttemptFilter & { timezone?: string }) => api.download(`/v1/admin/attempts.csv${qs({ ...p })}`),
  /** Per-lesson aggregates; editors too. */
  /** Admin only: mock exam results with learner names. */
  examAttempts: (p: ExamAttemptFilter & { limit?: number; offset?: number }) =>
    http.get<Page<AdminExamAttempt>>(`/v1/admin/exam-attempts${qs({ ...p })}`),
  /** Admin only: one exam with graded answers and its timing session. */
  examAttempt: (id: string) => http.get<AdminExamAttemptDetail>(`/v1/admin/exam-attempts/${id}`),
  lessonStats: (p: Omit<AttemptFilter, "user_id"> & { sort?: "attempts" | "accuracy"; top?: number; limit?: number; offset?: number }) =>
    http.get<Page<LessonStat>>(`/v1/admin/lesson-stats${qs({ ...p })}`),
};

/** The base path of one essay's actions: by attempt + exercise, or by mistake review. */
export const essayPath = (e: EssayRef) =>
  e.source === "mistake_review"
    ? `/v1/admin/essays/mistake-reviews/${e.review_id}`
    : `/v1/admin/essays/${e.attempt_id}/${e.exercise_id}`;

/** Essays waiting for a manual grade ("Chấm bài luận"). Editors and admins. */
export const essaysApi = {
  pending: (p: { language?: string; lesson_id?: string; limit?: number; offset?: number }) =>
    http.get<Page<PendingEssay>>(`/v1/admin/essays/pending${qs(p)}`),
  /** 422 validation_failed (criteria.N.points…) when it does not fit the rubric. Re-grading overwrites. */
  grade: (essay: EssayRef, body: EssayGradeInput) => http.post<EssayGradeResult>(`${essayPath(essay)}/grade`, body),
  /** Retries exam_writing_grade (ai_* errors pass through) and applies its result. */
  aiGrade: (essay: EssayRef) => http.post<EssayGradeResult>(`${essayPath(essay)}/ai-grade`, {}),
  /** Takes a pending essay out of the queue without a grade (409 essay_not_pending; reason required, audited). */
  dismiss: (essay: EssayRef, reason: string) => http.post<EssayGradeResult>(`${essayPath(essay)}/dismiss`, { reason }),
};

/** Editable reading document; on PUT `language_code` is ignored (immutable). */
export type ReadingInput = Pick<Reading,
  "level_id" | "slug" | "title" | "summary" | "body" | "audio_url" | "access_tier" | "status" | "sort_order"> & {
  language_code?: string;
  /** '' clears the cover; omitted (AI drafts) = none. */
  cover_url?: string;
};

export const readingsApi = {
  list: (p: { language?: string; level_id?: string; status?: string; q?: string; limit?: number; offset?: number }) =>
    http.get<Page<ReadingSummary>>(`/v1/admin/readings${qs(p)}`),
  get: (id: string) => http.get<Reading>(`/v1/admin/readings/${id}`),
  create: (body: ReadingInput) => http.post<Reading>("/v1/admin/readings", body),
  replace: (id: string, body: ReadingInput) => http.put<Reading>(`/v1/admin/readings/${id}`, body),
  remove: (id: string) => http.del(`/v1/admin/readings/${id}`),
};

export const notificationsApi = {
  broadcast: (body: BroadcastInput) => http.post<BroadcastResult>("/v1/admin/notifications/broadcast", body),
  /** Test push to the devices of the learner with this e-mail (verifies FCM end to end). */
  sendTest: (email: string) => http.post<TestPushResult>("/v1/admin/notifications/test", { email }),
  audience: (p: { segment: string; kind: BroadcastKind }) =>
    http.get<NotificationAudience>(`/v1/admin/notifications/audience${qs(p)}`),
  outbox: (p: { status?: OutboxStatus | ""; limit?: number; offset?: number }) =>
    http.get<Page<OutboxItem>>(`/v1/admin/notifications/outbox${qs(p)}`),
};

export type AuditFilter = {
  actor_id?: string;
  entity_type?: AuditEntityType | "";
  entity_id?: string;
  action?: AuditAction | "";
  /** RFC 3339; `from` inclusive, `to` exclusive. */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

/** Audit log of staff operations. Admin only and read-only: records are never changed or deleted. */
export const auditApi = {
  list: (p: AuditFilter) => http.get<Page<AuditEvent>>(`/v1/admin/audit-events${qs(p)}`),
};

export type FeedbackFilter = {
  category?: FeedbackCategory | "";
  status?: FeedbackStatus | "";
  platform?: FeedbackPlatform | "";
  app_version?: string;
  limit?: number;
  offset?: number;
};

/** Learners' in-app feedback ("Góp ý"). Admin only: messages are free text tied to a learner id. */
export const feedbackApi = {
  list: (p: FeedbackFilter) => http.get<Page<Feedback>>(`/v1/admin/feedback${qs(p)}`),
  triage: (id: string, body: { category?: FeedbackCategory; status?: FeedbackStatus }) =>
    http.patch<Feedback>(`/v1/admin/feedback/${id}`, body),
};

export type AnalyticsFilter = {
  /** YYYY-MM-DD local days (Asia/Ho_Chi_Minh), both inclusive. */
  from: string;
  to: string;
  /** "" = every learner. */
  language?: string;
};

/** First-party behaviour analytics. Admin only; aggregates, never a learner. */
export const analyticsApi = {
  summary: (p: AnalyticsFilter) => http.get<AnalyticsSummary>(`/v1/admin/analytics/summary${qs(p)}`),
  events: (p: AnalyticsFilter & { name: AnalyticsEventName }) =>
    http.get<AnalyticsSeries>(`/v1/admin/analytics/events${qs({ ...p, group_by: "day" })}`),
};

export type AiUsageFilter = {
  /** YYYY-MM-DD, both inclusive. */
  from: string;
  to: string;
  feature?: string;
  group_by: AiUsageGroupBy;
};

/** AI (OpenAI-compatible) configuration. Admin only; keys are write-only (the API returns a hint). */
export const aiApi = {
  settings: () => http.get<AiSettings>("/v1/admin/ai/settings"),
  saveSettings: (body: AiSettingsInput) => http.put<AiSettings>("/v1/admin/ai/settings", body),
  providers: () => http.get<List<AiProvider>>("/v1/admin/ai/providers"),
  createProvider: (body: AiProviderInput) => http.post<AiProvider>("/v1/admin/ai/providers", body),
  /** `api_key` omitted keeps the stored key, "" clears it. */
  updateProvider: (id: string, body: Partial<AiProviderInput>) => http.patch<AiProvider>(`/v1/admin/ai/providers/${id}`, body),
  removeProvider: (id: string) => http.del(`/v1/admin/ai/providers/${id}`),
  testProvider: (id: string) => http.post<AiProviderTestResult>(`/v1/admin/ai/providers/${id}/test`),
  /** Live list from the provider's GET {base_url}/models. */
  models: (id: string) => http.get<List<AiModel>>(`/v1/admin/ai/providers/${id}/models`),
  prices: () => http.get<List<AiModelPrice>>("/v1/admin/ai/prices"),
  /** Replaces the whole price table. */
  savePrices: (items: AiModelPrice[]) => http.put<List<AiModelPrice>>("/v1/admin/ai/prices", { items }),
  features: () => http.get<List<AiFeature>>("/v1/admin/ai/features"),
  /** Null fields use the feature's default. */
  saveFeature: (key: string, body: AiFeatureOverride) => http.put<AiFeature>(`/v1/admin/ai/features/${key}`, body),
  /** Drops the override: back to the defaults. */
  resetFeature: (key: string) => http.del(`/v1/admin/ai/features/${key}`),
  /** Runs the feature once with arbitrary vars (never cached). */
  playground: (key: string, body: AiPlaygroundInput) => http.post<AiPlaygroundResult>(`/v1/admin/ai/features/${key}/playground`, body),
  usage: (p: AiUsageFilter) => http.get<AiUsageReport>(`/v1/admin/ai/usage${qs(p)}`),
};

// --- AI group E (speaking) ---
export type AiRoleplayFilter = { language?: string; status?: AiRoleplayStatus | "" };

/** Role-play scenarios for the "roleplay" AI feature (editors and admins). */
export const aiRoleplaysApi = {
  list: (p: AiRoleplayFilter) => http.get<List<AiRoleplay>>(`/v1/admin/ai/roleplays${qs(p)}`),
  get: (id: string) => http.get<AiRoleplay>(`/v1/admin/ai/roleplays/${id}`),
  create: (body: AiRoleplayInput) => http.post<AiRoleplay>("/v1/admin/ai/roleplays", body),
  update: (id: string, body: AiRoleplayInput) => http.put<AiRoleplay>(`/v1/admin/ai/roleplays/${id}`, body),
  remove: (id: string) => http.del(`/v1/admin/ai/roleplays/${id}`),
};
// --- end of AI group E (speaking) ---

// ---------- AI group G: content tools (Công cụ nội dung) ----------
// Drafts only: nothing is saved until the editor saves through the content APIs above.
export const aiToolsApi = {
  generateExercises: (body: AiExerciseDraftsInput) => http.post<AiExerciseDraftsResult>("/v1/admin/ai/generate/exercises", body),
  /** A theory draft for a unit (validated, never saved by the server). */
  generateUnitTheory: (body: AiUnitTheoryInput) => http.post<{ theory: UnitTheory }>("/v1/admin/ai/generate/unit-theory", body),
  generateReading: (body: AiReadingDraftInput) => http.post<{ draft: AiReadingDraft }>("/v1/admin/ai/generate/reading", body),
  generateDistractors: (body: AiDistractorsInput) => http.post<{ distractors: string[] }>("/v1/admin/ai/generate/distractors", body),
  reviewLesson: (lessonId: string) => http.post<AiContentReview>(`/v1/admin/ai/review/lessons/${lessonId}`),
  translate: (body: AiTranslateInput) => http.post<AiTranslateResult>("/v1/admin/ai/translate", body),
  /** Admin only. */
  summarizeFeedback: (body: AiFeedbackSummaryInput) => http.post<AiFeedbackSummary>("/v1/admin/ai/feedback/summarize", body),
  /** Admin only. */
  draftNotification: (body: AiNotificationDraftInput) =>
    http.post<{ variants: AiNotificationVariant[] }>("/v1/admin/ai/notifications/draft", body),
};

/** In-app banners on the Learn tab ("Banner quảng cáo"). Editors and admins. */
export const promosApi = {
  /** Every promo, newest first, each with its computed `status`. */
  list: () => http.get<List<Promo>>("/v1/admin/promos"),
  create: (body: PromoInput) => http.post<Promo>("/v1/admin/promos", body),
  update: (id: string, body: Partial<PromoInput>) => http.patch<Promo>(`/v1/admin/promos/${id}`, body),
  remove: (id: string) => http.del(`/v1/admin/promos/${id}`),
};

/** PDF/DOCX read by the server's isolated reader, for use as an AI source (editors+). */
export const documentsApi = {
  /** The text by page (PDF) or paragraph (DOCX); refusals as for course imports (503 extraction_busy + Retry-After). */
  extract: (file: File, onProgress: (fraction: number) => void, signal?: AbortSignal) => {
    const form = new FormData();
    form.append("file", file);
    return uploadForm<ExtractedDocument>("/v1/admin/documents/extract", form, onProgress, signal);
  },
};

/** What starts an import (multipart: the file plus these fields). */
export interface CourseImportInput {
  file: File;
  /** Required for a course import; a unit import takes its course's. */
  language_code?: string;
  level_id?: string;
  /** A unit import into this course (language and level are the course's). */
  course_id?: string;
  /** Course (unit import: unit) title hint. */
  title?: string;
  /** Extra instructions for the AI (≤ 1000 characters). */
  instructions?: string;
}

/**
 * "Tạo khóa học từ tài liệu": a PDF/DOCX becomes a course draft in a
 * background job; editors review the draft, then apply creates the course.
 */
export const courseImportsApi = {
  /** 202 with the queued import; progress of the upload goes to onProgress. */
  create: (input: CourseImportInput, onProgress: (fraction: number) => void, signal?: AbortSignal) => {
    const form = new FormData();
    form.append("file", input.file);
    if (input.course_id) form.append("course_id", input.course_id);
    if (input.language_code) form.append("language_code", input.language_code);
    if (input.level_id) form.append("level_id", input.level_id);
    if (input.title?.trim()) form.append("title", input.title.trim());
    if (input.instructions?.trim()) form.append("instructions", input.instructions.trim());
    return uploadForm<CourseImport>("/v1/admin/course-imports", form, onProgress, signal);
  },
  /** Newest first; editors see their own imports, admins everyone's. `course_id`: unit imports into that course. */
  list: (p: { status?: CourseImportStatus | ""; mode?: CourseImportMode | ""; course_id?: string; limit?: number; offset?: number }) =>
    http.get<{ items: CourseImportSummary[]; total: number }>(`/v1/admin/course-imports${qs(p)}`),
  get: (id: string) => http.get<CourseImport>(`/v1/admin/course-imports/${id}`),
  /** Only while ready; 422 with paths such as units.0.lessons.1.exercises.2.content.options. */
  saveDraft: (id: string, draft: CourseDraft) => http.put<CourseImport>(`/v1/admin/course-imports/${id}/draft`, draft),
  /**
   * Creates the course (draft status) and everything in it in one go, or for
   * a unit import appends the unit to its course; the import becomes applied.
   */
  apply: (id: string) => http.post<{ course_id: string; unit_id?: string | null }>(`/v1/admin/course-imports/${id}/apply`),
  cancel: (id: string) => http.post<CourseImport>(`/v1/admin/course-imports/${id}/cancel`),
  /** A failed import resumes from the step that failed. */
  retry: (id: string) => http.post<CourseImport>(`/v1/admin/course-imports/${id}/retry`),
  /** Refused (import_running) while it runs. */
  remove: (id: string) => http.del(`/v1/admin/course-imports/${id}`),
};

// ---------- Language centers (docs/b2b-centers-contract.md §4–§5) ----------

/** Woodpecker admins: centers, their licence and center admins, the seat price list. */
export const orgsApi = {
  list: (p: { q?: string; status?: string }) => http.get<List<AdminOrg>>(`/v1/admin/orgs${qs(p)}`),
  get: (id: string) => http.get<AdminOrg>(`/v1/admin/orgs/${id}`),
  create: (body: AdminOrgInput) => http.post<AdminOrg>("/v1/admin/orgs", body),
  update: (id: string, body: AdminOrgInput) => http.patch<AdminOrg>(`/v1/admin/orgs/${id}`, body),
  addAdmin: (id: string, staffId: string) => http.put<void>(`/v1/admin/orgs/${id}/admins/${staffId}`, {}),
  removeAdmin: (id: string, staffId: string) => http.del(`/v1/admin/orgs/${id}/admins/${staffId}`),
  tiers: () => http.get<List<PricingTier>>("/v1/admin/org-pricing-tiers"),
  saveTiers: (items: PricingTier[]) => http.put<List<PricingTier>>("/v1/admin/org-pricing-tiers", { items }),
};

export type StudentFilter = { q?: string; class_id?: string; at_risk?: "true" | ""; days?: number; limit?: number; offset?: number };

/** The center dashboard (center admins; admins for support). */
export const centerApi = {
  orgs: () => http.get<List<OrgSummary>>("/v1/center/orgs"),
  overview: (org: string) => http.get<CenterOverview>(`/v1/center/orgs/${org}`),
  activity: (org: string, days = 30) => http.get<{ days: OrgActivityDay[] }>(`/v1/center/orgs/${org}/activity${qs({ days })}`),
  branches: (org: string) => http.get<List<OrgBranch>>(`/v1/center/orgs/${org}/branches`),
  createBranch: (org: string, body: { name: string; address?: string }) => http.post<OrgBranch>(`/v1/center/orgs/${org}/branches`, body),
  updateBranch: (org: string, id: string, body: { name: string; address?: string }) =>
    http.patch<OrgBranch>(`/v1/center/orgs/${org}/branches/${id}`, body),
  deleteBranch: (org: string, id: string) => http.del(`/v1/center/orgs/${org}/branches/${id}`),
  teachers: (org: string) => http.get<List<OrgTeacher>>(`/v1/center/orgs/${org}/teachers`),
  addTeacher: (org: string, body: { email: string; branch_id?: string }) => http.post<OrgTeacher>(`/v1/center/orgs/${org}/teachers`, body),
  setTeacherBranch: (org: string, userId: string, branchId: string | null) =>
    http.patch<OrgTeacher>(`/v1/center/orgs/${org}/teachers/${userId}`, branchId ? { branch_id: branchId } : { clear_branch: true }),
  removeTeacher: (org: string, userId: string) => http.del(`/v1/center/orgs/${org}/teachers/${userId}`),
  linkableClasses: (org: string, userId: string) =>
    http.get<List<LinkableClass>>(`/v1/center/orgs/${org}/teachers/${userId}/linkable-classes`),
  /** Links the teacher's earlier classes; their members take seats (all or nothing, 409 org_seats_full). */
  linkClasses: (org: string, userId: string, classroomIds: string[]) =>
    http.post<{ linked: string[]; students_enrolled: number }>(`/v1/center/orgs/${org}/teachers/${userId}/link-classes`,
      { classroom_ids: classroomIds }),
  classes: (org: string) => http.get<List<OrgClass>>(`/v1/center/orgs/${org}/classes`),
  setClassBranch: (org: string, id: string, branchId: string | null) =>
    http.patch<OrgClass>(`/v1/center/orgs/${org}/classes/${id}`, branchId ? { branch_id: branchId } : { clear_branch: true }),
  classReport: (org: string, id: string, days = 14) => http.get<ClassReport>(`/v1/center/orgs/${org}/classes/${id}/report${qs({ days })}`),
  students: (org: string, p: StudentFilter) => http.get<Page<OrgStudent>>(`/v1/center/orgs/${org}/students${qs(p)}`),
  studentsCsv: (org: string, days = 30) => api.download(`/v1/center/orgs/${org}/students.csv${qs({ days })}`),
  updateStudent: (org: string, userId: string, body: { external_ref?: string; parent_email?: string; parent_consent?: boolean; clear_parent_email?: boolean }) =>
    http.patch<OrgStudent>(`/v1/center/orgs/${org}/students/${userId}`, body),
  removeStudent: (org: string, userId: string) => http.del(`/v1/center/orgs/${org}/students/${userId}`),
  parentPreview: (org: string, userId: string) =>
    http.get<{ subject: string; html: string; text: string }>(`/v1/center/orgs/${org}/students/${userId}/parent-report/preview`),
  invites: (org: string, status: InviteStatus | "" = "") => http.get<List<OrgInvite>>(`/v1/center/orgs/${org}/invites${qs({ status })}`),
  generateInvites: (org: string, body: { count: number; classroom_id?: string; expires_at?: string }) =>
    http.post<List<OrgInvite>>(`/v1/center/orgs/${org}/invites`, body),
  importInvites: (org: string, csv: string) =>
    http.post<{ items: OrgInvite[]; errors: { line: number; message: string }[] }>(`/v1/center/orgs/${org}/invites/import`, { csv }),
  invitesCsv: (org: string, status: InviteStatus | "" = "") => api.download(`/v1/center/orgs/${org}/invites.csv${qs({ status })}`),
  revokeInvite: (org: string, id: string) => http.del(`/v1/center/orgs/${org}/invites/${id}`),
};
